import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';
import { Bot, ChevronDown, Headset, MessageCircle, Pin, Star, X } from 'lucide-react';
import ChatComposer from '../chat/ChatComposer';
import ChatAttachment from '../chat/ChatAttachment';
import { DeletedText, ForwardedTag, MessageFlags, PinnedBanner, Reactions, ReplyQuote } from '../chat/MessageParts';
import useMessageMenu from '../chat/useMessageMenu';
import { SavedMessagesDialog } from '../chat/ChatDialogs';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const uploadChatFile = async (file, onProgress) => {
    const data = new FormData();
    data.append('file', file);
    const res = await axios.post(`${API}/api/chat-uploads`, data, { ...authHeaders(), onUploadProgress: e => onProgress?.(Math.min(100, Math.round((e.loaded / (e.total || file.size || 1)) * 100))) });
    return res.data.attachment;
};

const SENDER = {
    bot: { label: 'Assistant', Icon: Bot },
    admin: { label: 'Support team', Icon: Headset },
};

const SupportChatWidget = ({ open, onClose }) => {
    const { user } = useAuth();
    const [messages, setMessages] = useState([]);
    const scrollRef = useRef(null);
    const [replyTarget, setReplyTarget] = useState(null);
    const [editTarget, setEditTarget] = useState(null);
    const [savedKind, setSavedKind] = useState(null);


    // while any chat window is open, the floating "Need help?" launcher steps aside
    useEffect(() => {
        if (!open) return undefined;
        document.body.classList.add("support-chat-open");
        return () => document.body.classList.remove("support-chat-open");
    }, [open]);
    const fetchChat = async () => {
        try {
            const res = await axios.get(`${API}/api/support/my`, authHeaders());
            setMessages(res.data.chat?.messages || []);
        } catch (err) { }
    };

    // The thread is stored per user, so reopening the chat continues exactly where it stopped.
    useEffect(() => {
        if (!(open && user)) return;
        fetchChat();
        const s = io(API, { auth: (cb) => cb({ token: localStorage.getItem('token') }) }); // the server checks this token
        s.on('supportChatUpdate', fetchChat);               // admin replied → show it live
        const interval = setInterval(fetchChat, 30000);     // safety net if the socket drops
        return () => { s.disconnect(); clearInterval(interval); };
    }, [open, user]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages]);

    // React / star / pin / edit / delete one message.
    const performAction = async (item, action, payload = {}) => {
        try {
            const res = await axios.post(`${API}/api/support/my/messages/${item._id}/action`, { action, ...payload }, authHeaders());
            setMessages(current => (res.data.removed
                ? current.filter(m => m._id !== item._id)
                : current.map(m => (m._id === item._id ? { ...m, ...res.data.item } : m))));
            if (action === 'delete' && replyTarget?._id === item._id) setReplyTarget(null);
            return true;
        } catch (err) {
            toast.error(err.response?.data?.message || 'Could not update the message');
            return false;
        }
    };

    const menu = useMessageMenu({
        onAction: performAction,
        onReply: item => { setEditTarget(null); setReplyTarget(item); },
        onEdit: item => { setReplyTarget(null); setEditTarget(item); },
        onForward: () => { },
        isOwn: item => item.from === 'user',
        canForward: () => false, // no other conversations to forward to in this window
        attachmentBase: API,
    });

    const authorOf = (from) => (from === 'user' ? 'You' : from === 'bot' ? 'Assistant' : 'Support team');

    // Text and / or an uploaded file. Resolves true when sent so the composer clears.
    const handleSend = async ({ text, attachment, editing }) => {
        if (editing) return performAction(editing, 'edit', { text });
        try {
            const res = await axios.post(`${API}/api/support/my/send`, { text, attachment, replyToId: replyTarget?._id }, authHeaders());
            setReplyTarget(null);
            setMessages(res.data.chat.messages);
            return true;
        } catch (err) {
            toast.error(err.response?.data?.message || 'Could not send message');
            return false;
        }
    };

    if (!open) return null;

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.panel} onClick={e => e.stopPropagation()}>
                <div style={styles.header}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={styles.headerIcon}><Bot size={19} color="#fff" /></span>
                        <div>
                            <div style={styles.headerTitle}>Gettimelam Assistant</div>
                            <div style={styles.headerSub}>Instant answers · our team steps in when needed</div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                        <button style={styles.closeBtn} onClick={() => setSavedKind('pinned')} aria-label="Pinned messages" title="Pinned messages"><Pin size={13} /></button>
                        <button style={styles.closeBtn} onClick={() => setSavedKind('starred')} aria-label="Starred messages" title="Starred messages"><Star size={13} /></button>
                        <button style={styles.closeBtn} onClick={onClose} aria-label="Close chat"><X size={14} /></button>
                    </div>
                </div>

                <PinnedBanner items={messages} onOpen={() => setSavedKind('pinned')} />
                <div style={styles.messages} ref={scrollRef}>
                    {messages.length === 0 ? (
                        <div style={styles.emptyState}>
                            <MessageCircle size={30} color="var(--c-ddbb88, #ddbb88)" />
                            <p style={{ marginTop: '10px' }}>Have a question or facing an issue?<br />Send us a message below.</p>
                        </div>
                    ) : (
                        messages.map((m, i) => {
                            const mine = m.from === 'user';
                            const sender = SENDER[m.from];
                            return (
                                <div key={m._id || i} style={{ ...styles.row, justifyContent: mine ? 'flex-end' : 'flex-start' }}>
                                    {!mine && sender && (
                                        <span style={{ ...styles.avatar, background: m.from === 'bot' ? '#8B1A1A' : '#2E7D32' }}>
                                            <sender.Icon size={14} color="#fff" />
                                        </span>
                                    )}
                                    <div className="msg-bubble-wrap" style={{ maxWidth: '78%' }} {...menu.bind(m)}>
                                        <div data-own={mine ? 'true' : 'false'} style={{ ...styles.bubble, maxWidth: '100%', ...(mine ? styles.bubbleUser : styles.bubbleAdmin) }}>
                                            {!mine && sender && <div style={styles.senderLabel}>{sender.label}</div>}
                                            <ForwardedTag item={m} />
                                            <ReplyQuote reply={m.replyTo} author={authorOf(m.replyTo?.from)} />
                                            {m.deleted ? <DeletedText /> : (
                                                <>
                                                    <ChatAttachment attachment={m.attachment} base={API} />
                                                    {m.text && <div style={{ whiteSpace: 'pre-wrap' }}>{m.text}</div>}
                                                </>
                                            )}
                                            <div style={styles.bubbleTime}>
                                                <MessageFlags item={m} />
                                                {new Date(m.at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                        </div>
                                        {!mine && i === messages.length - 1 && m.suggestions && m.suggestions.length > 0 && (
                                            <div style={styles.chips}>
                                                {m.suggestions.map(choice => (
                                                    <button key={choice.question} type="button" style={styles.chip} onClick={() => handleSend({ text: choice.question })}>{choice.question}</button>
                                                ))}
                                            </div>
                                        )}
                                            <Reactions reactions={m.reactions} onToggle={emoji => performAction(m, 'react', { emoji })} end={mine} />
                                        <button type="button" className="msg-menu-btn" onClick={e => menu.openFrom(m, e)} aria-label="Message options"><ChevronDown size={14} /></button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                <ChatComposer
                    placeholder="Type your message..."
                    onSend={handleSend}
                    replyTo={replyTarget}
                    replyLabel={replyTarget ? authorOf(replyTarget.from) : ''}
                    onCancelReply={() => setReplyTarget(null)}
                    editing={editTarget}
                    onCancelEdit={() => setEditTarget(null)}
                    uploadFile={uploadChatFile}
                    onError={message => toast.error(message)}
                    fileBase={API}
                />
            </div>
            <div onClick={e => e.stopPropagation()}>
            {menu.element}
            {savedKind && (
                <SavedMessagesDialog
                    kind={savedKind}
                    fetchItems={async kind => messages.filter(m => (kind === 'starred' ? m.starred : m.pinned) && !m.deleted)}
                    labelFor={item => authorOf(item.from)}
                    onRemove={item => performAction(item, savedKind === 'starred' ? 'star' : 'pin')}
                    onClose={() => setSavedKind(null)}
                />
            )}
            </div>
        </div>
    );
};

const styles = {
    // "did you mean ...?" choices under the bot's message
    chips: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' },
    chip: { padding: '7px 12px', borderRadius: '999px', border: '1px solid var(--primary)', background: '#fff', color: 'var(--primary)', fontSize: '12.5px', fontWeight: 600, textAlign: 'left', cursor: 'pointer' },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', padding: '24px' },
    panel: {
        width: '380px', height: '540px', background: '#fff', borderRadius: '16px', overflow: 'hidden',
        display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
    },
    header: { background: 'linear-gradient(135deg, #8B1A1A, var(--dark))', padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    headerIcon: { width: '34px', height: '34px', borderRadius: '50%', background: 'rgba(255,255,255,0.18)', display: 'grid', placeItems: 'center' },
    headerTitle: { color: '#fff', fontWeight: '700', fontSize: '14.5px' },
    headerSub: { color: 'rgba(255,255,255,0.75)', fontSize: '11px', marginTop: '2px' },
    closeBtn: { background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: '28px', height: '28px', borderRadius: '50%', cursor: 'pointer', display: 'grid', placeItems: 'center' },
    messages: { flex: 1, overflowY: 'auto', padding: '16px', background: 'var(--c-fffdf6, #fffdf6)' },
    emptyState: { textAlign: 'center', color: 'var(--c-b0987a, #b0987a)', fontSize: '13px', marginTop: '60px', lineHeight: 1.6 },
    row: { display: 'flex', alignItems: 'flex-end', gap: '8px', marginBottom: '10px' },
    avatar: { width: '26px', height: '26px', borderRadius: '50%', display: 'grid', placeItems: 'center', flexShrink: 0 },
    bubble: { maxWidth: '78%', padding: '10px 13px', borderRadius: '14px', fontSize: '13.5px', lineHeight: 1.5 },
    bubbleUser: { background: '#8B1A1A', color: '#fff', borderBottomRightRadius: '3px' },
    bubbleAdmin: { background: '#fff', color: 'var(--c-3a2a20, #3a2a20)', border: '1px solid var(--c-f0e0c0, #f0e0c0)', borderBottomLeftRadius: '3px' },
    senderLabel: { fontSize: '10px', fontWeight: '800', letterSpacing: '0.4px', textTransform: 'uppercase', color: 'var(--c-b0987a, #b0987a)', marginBottom: '3px' },
    bubbleTime: { fontSize: '9.5px', opacity: 0.6, marginTop: '4px' },
    inputRow: { display: 'flex', gap: '8px', padding: '12px', borderTop: '1px solid var(--c-f0e0c0, #f0e0c0)', background: '#fff' },
    input: { flex: 1, padding: '10px 14px', border: '1.5px solid var(--c-e0d0b0, #e0d0b0)', borderRadius: '999px', fontSize: '13px', outline: 'none' },
    sendBtn: { width: '40px', height: '40px', background: '#8B1A1A', color: '#fff', border: 'none', borderRadius: '50%', cursor: 'pointer', display: 'grid', placeItems: 'center' },
};

export default SupportChatWidget;
